import { c as _c } from "react/compiler-runtime";
// Оформление заказа — по макету «5.1 Оформление заказа-2» (Figma ЛК
// Покупателя, кадры 1200). Колонка 753: «Доставка» (превью 77×102 r4,
// карточка #F8F8F8 r12 с вкладками «В пункт выдачи / Курьером», строкой
// адреса и слотами-тарифами курьера), «Получатель» (r16, модалки выбора/
// добавления), «Способ оплаты» (карточки 100×68 r16). Справа сводка 367
// (#F8F8F8 r16, промокод, итого) и тёмная кнопка 367×48 r12.
//
// Логика доставки/ПВЗ/баланса/счёта/оплаты прежняя: квота по магазинам,
// один выбор на группу, ПВЗ для СДЭК-тарифов «до пункта», списание баланса,
// счёт ЮЛ, CloudPayments с поллингом confirm. Получатели, ранее выбранные
// ПВЗ и детали адреса хранятся на шлюзе (миграция 069).
import { useQuery } from "@tanstack/react-query";
import { markReferralApplied, referralApplyPendingFor } from "@/lib/referral-promo";
import { ecPurchaseFromOrder, METRIKA_GOALS, reachGoal, reachGoalOnce } from "@/lib/metrika-ecommerce";
import { Landmark } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router";
import { toast } from "sonner";
import { trackOrderCreated } from "@/lib/discovery";
import { api, type BuyerAddress, type BuyerPaymentMethod, type BuyerRecipient, type CartItem, type CreateOrderRequest, type DeliveryOption, type DeliveryQuoteGroup, type DeliverySelection, type Invoice, type PickupPoint, type SavedPickupPoint, type SelfDeliveryShopOption, type SellerPickupPoint, type SellerPickupShopOption } from "@/api";
import { AddressEditModal, AddressListModal, courierAddressInput, courierAddressLabel, courierValuesFromAddress, emptyCourierAddress, type CourierAddressValues } from "@/components/checkout/address-modals";
import { DeliveryPicker, type PickerTab } from "@/components/checkout/delivery-picker";
import { AddCardModal, EmailModal, InvoicePreviewModal, InvoiceRequisitesModal, OrderDoneModal } from "@/components/checkout/payment-modals";
import { carrierTitle, PickupInfoModal, PickupListModal } from "@/components/checkout/pickup-modals";
import { RecipientFormModal, RecipientListModal } from "@/components/checkout/recipient-modals";
import { CheckRow, phoneLabel } from "@/components/checkout/ui";
import { ChevronRight16Icon, ChevronRightStrokeIcon, WorkIcon } from "@/components/icons";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Chip } from "@/components/ui/chip";
import { Input } from "@/components/ui/text-field";
import { EmptyState } from "@/components/common/states";
import { useInvoicePrinter } from "@/components/orders/invoice-view";
import { isPaidOrderStatus } from "@/components/profile/order-view";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { loadCloudPayments, submitAcsForm } from "@/lib/cloudpayments";
import { clearDiscoverySource, currentDiscoverySource } from "@/lib/discovery-source";
import { readUgcArticleCode } from "@/lib/ugc-attribution";
import { useFormatPrice } from "@/lib/use-format-price";
import { SELF_DELIVERY_LABEL, selfFreeOverLeft, selfOfferMap, withSelfDelivery } from "@/lib/self-delivery";
import { SELLER_PICKUP_LABEL, sellerPickupOfferMap, withSellerPickup } from "@/lib/seller-pickup";
import { cn } from "@/lib/utils";
import { useCart, useProfile } from "@/store";
import { attachDeviceToAccount } from "@/store/device";
import { ShoppingBag } from "lucide-react";

// Does a quote option match a stored selection (so a still-valid pick survives a
// re-quote)? Tariff code identifies an СДЭК option; method identifies a flat one.
const sameOption = (o: DeliveryOption, sel: DeliverySelection): boolean => o.carrier === sel.carrier && o.tariffCode === sel.tariffCode && o.method === sel.method;

// Turn a picked option into the selection sent with the order. Pickup-point
// fields are filled in separately once the buyer picks a ПВЗ.
const toSelection = (shopId: string, o: DeliveryOption): DeliverySelection => ({
  shopId,
  carrier: o.carrier,
  method: o.method,
  tariffCode: o.tariffCode,
  tariffName: o.carrier === "sdek" ? o.label : undefined,
  deliveryDays: o.deliveryDays,
  deliverySum: o.deliverySum,
  toPickupPoint: o.toPickupPoint
});
const needsPickupPoint = (sel: DeliverySelection | undefined): boolean => Boolean(sel && sel.carrier === "sdek" && sel.toPickupPoint);

// Совпадает ли выбор с одной из опций группы — чтобы прочитать флаги примерки/
// частичного выкупа (они живут на опции квоты, а не в самом выборе).
const selectedOptionFor = (group: DeliveryQuoteGroup, sel: DeliverySelection | undefined): DeliveryOption | undefined => sel ? group.options.find(o => sameOption(o, sel)) : undefined;

// Оплата при получении (СДЭК наложенный платёж). Держим ВЫКЛ, пока договор СДЭК
// не разрешит наложенный платёж — флаг должен совпадать с бэкенд-флагом
// SDEK_COD_ENABLED. Включить только после подключения в договоре.
const COD_ENABLED = false;
type Mode = "pickup" | "courier";
const optionMode = (o: DeliveryOption): Mode => o.toPickupPoint ? "pickup" : "courier";

// Выбранная карточка оплаты: сохранённая карта/СБП, общий «СБП», новая карта
// (виджет CloudPayments), счёт ЮЛ, при получении.
type PayKey = `card:${string}` | `sbp:${string}` | "sbp" | "invoice" | "cod";
const payMethodOf = (key: PayKey): "card" | "sbp" | "invoice" | "cod" => key === "invoice" ? "invoice" : key === "cod" ? "cod" : key.startsWith("sbp") ? "sbp" : "card";
type Recipient = {
  id?: string;
  firstName: string;
  lastName: string;
  phone: string;
};
const LEGAL = {
  terms: `/docs/b2c/${encodeURIComponent("Пользовательское соглашение OSMIX.pdf")}`,
  delivery: `/docs/b2c/${encodeURIComponent("Правила доставки и примерки OSMIX.pdf")}`,
  pdnConsent: `/docs/b2c/${encodeURIComponent("Согласие на обработку персональных данных.pdf")}`
};
export function CheckoutPage() {
  const {
    t
  } = useTranslation();
  const fmt = useFormatPrice();
  const navigate = useNavigate();
  const cart = useCart(s => s.cart);
  const clearCart = useCart(s_0 => s_0.clear);
  const refreshCart = useCart(s_1 => s_1.refresh);
  const applyPromo = useCart(s_2 => s_2.applyPromo);
  const clearPromo = useCart(s_3 => s_3.clearPromo);
  const buyerUser = useProfile(s_4 => s_4.user);
  const buyerProfile = useProfile(s_5 => s_5.profile);
  const updateProfile = useProfile(s_6 => s_6.updateProfile);
  const printInvoice = useInvoicePrinter();
  const token = useProfile(s_7 => s_7.token);
  const hydrateMe = useProfile(s_8 => s_8.hydrateMe);
  useEffect(() => {
    if (token && !buyerUser) void hydrateMe().catch(() => undefined);
  }, [hydrateMe, token, buyerUser]);
  // Оформление под аккаунтом: корзина устройства должна быть корзиной
  // аккаунта (гость → аккаунт, store/device.ts). Обычно это уже сделал вход,
  // здесь — страховка для старых сессий; вызов идемпотентен.
  useEffect(() => {
    if (token) void attachDeviceToAccount();
  }, [token]);

  /*
   * Частичное оформление: корзина передаёт `?lines=` — строки, которые
   * покупатель оформляет («Оплатить» у товара под заказ, «К оформлению» во
   * вкладке). Товары, суммы, доставка и заказ — только по ним; без параметра —
   * вся корзина, как раньше. Скидку промокода шлюз в частичный заказ не
   * переносит, поэтому здесь её и не показываем.
   */
  const [searchParams] = useSearchParams();
  const linesParam = searchParams.get("lines") ?? "";
  const requestedLines = useMemo(() => linesParam.split(",").map(id => id.trim()).filter(Boolean), [linesParam]);
  const partial = requestedLines.length > 0;
  const cartItems = cart?.items ?? [];
  const orderLines = partial ? cartItems.filter(i => requestedLines.includes(i.id)) : cartItems;
  const lineIds = partial ? orderLines.map(i_0 => i_0.id) : undefined;
  const lineKey = lineIds?.join(",") ?? "";

  // ── Промокод (как в корзине: Enter/blur применяет, «Убрать» снимает) ──
  const [promoInput, setPromoInput] = useState("");
  const [promoBusy, setPromoBusy] = useState(false);
  // Пришёл по партнёрской ссылке ?promo= — код подставляется сам, один раз на
  // корзину: снятый покупателем код обратно не возвращаем. Не подошёл (другой
  // магазин, порог суммы) — молча: поле остаётся пустым, как без ссылки.
  const cartIdForReferral = cart?.id;
  const cartHasPromo = Boolean(cart?.promoCode);
  const cartHasItems = (cart?.items.length ?? 0) > 0;
  useEffect(() => {
    if (!cartIdForReferral || cartHasPromo || !cartHasItems) return;
    const code = referralApplyPendingFor(cartIdForReferral);
    if (!code) return;
    markReferralApplied(cartIdForReferral);
    void applyPromo(code).catch(() => undefined);
  }, [applyPromo, cartIdForReferral, cartHasPromo, cartHasItems]);
  const submitPromo = async () => {
    const code_0 = promoInput.trim();
    if (!code_0 || promoBusy) return;
    setPromoBusy(true);
    try {
      await applyPromo(code_0);
      setPromoInput("");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Промокод не применён");
    } finally {
      setPromoBusy(false);
    }
  };
  const dropPromo = async () => {
    if (promoBusy) return;
    setPromoBusy(true);
    try {
      await clearPromo();
    } finally {
      setPromoBusy(false);
    }
  };

  // ── Сохранённые данные покупателя ──
  const userKey = buyerUser?.id ?? "guest";
  const addressesQ = useQuery({
    queryKey: ["buyer", "listAddresses", userKey],
    queryFn: () => buyerUser ? api.buyer.listAddresses() : Promise.resolve([] as BuyerAddress[])
  });
  const recipientsQ = useQuery({
    queryKey: ["buyer", "recipients", userKey],
    queryFn: () => buyerUser ? api.buyer.listRecipients() : Promise.resolve([] as BuyerRecipient[])
  });
  const savedPointsQ = useQuery({
    queryKey: ["buyer", "pickupPoints", userKey],
    queryFn: () => buyerUser ? api.buyer.listPickupPoints() : Promise.resolve([] as SavedPickupPoint[])
  });
  const methodsQ = useQuery({
    queryKey: ["buyer", "paymentMethods", userKey],
    queryFn: () => buyerUser ? api.buyer.listPaymentMethods() : Promise.resolve([] as BuyerPaymentMethod[])
  });
  const balanceQuery = useQuery({
    queryKey: ["buyer", "balance", userKey],
    queryFn: () => buyerUser ? api.buyer.getBalance() : Promise.resolve(null)
  });
  const billingQuery = useQuery({
    queryKey: ["buyer", "billingProfile", userKey],
    queryFn: () => buyerUser ? api.buyer.getBillingProfile() : Promise.resolve(null)
  });
  const addresses = addressesQ.data ?? [];
  const recipients = recipientsQ.data ?? [];
  const savedPoints = savedPointsQ.data ?? [];
  // fd-payment-01: в оформлении — только карты, которыми можно заплатить
  // (сохранены через платёжную систему), и СБП. Подписи карт без токена
  // оплату не ускоряли — при выборе карту всё равно вводили заново.
  const methods = (methodsQ.data ?? []).filter(m => m.chargeable === true || m.provider === "sbp");
  const payConfigQ = useQuery({
    queryKey: ["payments", "config"],
    queryFn: () => api.payments.config(),
    staleTime: 300_000
  });
  const savedCardsOn = Boolean(buyerUser) && payConfigQ.data?.savedCards === true;
  const billing = billingQuery.data ?? null;

  // ── Адрес/город назначения ──
  const [courier, setCourier] = useState<CourierAddressValues>(emptyCourierAddress);
  const [addressId, setAddressId] = useState<string | null>(null);
  // region - из подсказки DaData при выборе города; шлюз без него определит
  // регион по городу сам (fd-analytics-04).
  const [dest, setDest] = useState<{
    country: string;
    city: string;
    postalCode: string;
    region?: string;
  }>({
    country: "Россия",
    city: "",
    postalCode: ""
  });
  const destTouched = useRef(false);
  const applyAddress = (a: BuyerAddress) => {
    setAddressId(a.id);
    setCourier(courierValuesFromAddress(a));
    setDest({
      country: a.country || "Россия",
      city: a.city,
      postalCode: a.postalCode
    });
    destTouched.current = true;
  };
  useEffect(() => {
    if (destTouched.current || addresses.length === 0) return;
    const a_0 = addresses.find(x => x.isDefault) ?? addresses[0];
    if (a_0) applyAddress(a_0);
    // biome-ignore lint/correctness/useExhaustiveDependencies: seed once from saved addresses
  }, [addresses]);
  // Без сохранённых адресов стартуем с города последнего ПВЗ.
  useEffect(() => {
    if (destTouched.current || addresses.length > 0 || savedPoints.length === 0) return;
    const p = savedPoints[0];
    if (p?.city) setDest(d => ({
      ...d,
      city: p.city
    }));
  }, [savedPoints, addresses.length]);

  // ── Получатель ──
  const [recipient, setRecipient] = useState<Recipient | null>(null);
  useEffect(() => {
    // Сохранённый получатель важнее подставленного из профиля.
    if (recipient && (recipient.id || recipients.length === 0)) return;
    const def = recipients.find(r => r.isDefault) ?? recipients[0];
    if (def) {
      setRecipient({
        id: def.id,
        firstName: def.firstName,
        lastName: def.lastName,
        phone: def.phone
      });
      return;
    }
    if (buyerProfile && !recipient) {
      const [first = "", ...rest] = (buyerProfile.name || "").split(" ");
      setRecipient({
        firstName: buyerProfile.firstName || first,
        lastName: buyerProfile.lastName || rest.join(" "),
        phone: buyerProfile.phone
      });
    }
  }, [recipients, buyerProfile, recipient]);
  const [submitting, setSubmitting] = useState(false);

  // ── Способ оплаты ──
  const [payKey, setPayKey] = useState<PayKey>("card:new");
  const payMethod = payMethodOf(payKey);
  // Согласие «Сохранить карту» (fd-payment-01) — только для оплаты новой картой.
  const [saveCard, setSaveCard] = useState(false);
  const savedCardId = payKey.startsWith("card:") && payKey !== "card:new" ? payKey.slice("card:".length) : null;
  useEffect(() => {
    if (payKey !== "card:new" || methods.length === 0) return;
    const def_0 = methods.find(m_0 => m_0.isDefault) ?? methods[0];
    if (def_0) setPayKey(def_0.provider === "sbp" ? `sbp:${def_0.id}` : `card:${def_0.id}`);
    // biome-ignore lint/correctness/useExhaustiveDependencies: default once methods land
  }, [methodsQ.data]);
  // Списание с баланса (fd-payment-03): вкл/выкл + сумма.
  const [useBalance, setUseBalance] = useState(false);
  const [balanceInput, setBalanceInput] = useState("");
  // fd-promo-16: товары со скидкой по промокоду — отдельным заказом.
  const [splitPromo, setSplitPromo] = useState(false);

  // Active payment poll, so it can be cancelled on unmount (or a new submit).
  const pollRef = useRef<{
    cancelled: boolean;
  } | null>(null);
  useEffect(() => () => {
    if (pollRef.current) pollRef.current.cancelled = true;
  }, []);

  // ── Квота доставки ──
  const quote = useQuery({
    queryKey: ["delivery", "quote", cart?.id, dest.country, dest.city, dest.postalCode, lineKey],
    queryFn: () => cart && dest.city && (!lineIds || lineIds.length > 0) ? api.delivery.quote({
      cartId: cart.id,
      country: dest.country,
      city: dest.city,
      postalCode: dest.postalCode || undefined,
      ...(lineIds ? {
        lineIds
      } : {})
    }) : Promise.resolve({
      groups: [] as DeliveryQuoteGroup[]
    })
  });
  // Своя доставка продавцов в город покупателя. Сбой (например, лимит
  // запросов) не должен ломать оформление — тогда вариантов просто нет.
  const selfQ = useQuery({
    queryKey: ["delivery", "self", cart?.id, dest.country, dest.city, lineKey],
    queryFn: () => cart && dest.city && (!lineIds || lineIds.length > 0) ? api.delivery.selfOptions({
      cartId: cart.id,
      city: dest.city,
      country: dest.country,
      lineIds
    }).catch(() => [] as SelfDeliveryShopOption[]) : Promise.resolve([] as SelfDeliveryShopOption[]),
    retry: false
  });
  // Самовывоз от продавцов корзины (точки магазинов). Сбой — вариантов нет.
  const pickupQ = useQuery({
    queryKey: ["delivery", "seller-pickup", cart?.id, dest.country, dest.city, lineKey],
    queryFn: () => cart && dest.city && (!lineIds || lineIds.length > 0) ? api.delivery.sellerPickupOptions({
      cartId: cart.id,
      city: dest.city,
      country: dest.country,
      lineIds
    }).catch(() => [] as SellerPickupShopOption[]) : Promise.resolve([] as SellerPickupShopOption[]),
    retry: false
  });
  const groups = withSellerPickup(withSelfDelivery(quote.data?.groups ?? [], selfQ.data ?? []), pickupQ.data ?? []);
  const selfOffers = selfOfferMap(selfQ.data ?? []);
  const pickupOffers = sellerPickupOfferMap(pickupQ.data ?? []);
  const modeAvailable = (m_1: Mode) => groups.length > 0 && groups.every(g => g.options.some(o => optionMode(o) === m_1 && !o.unavailable));
  const [mode, setMode] = useState<Mode>("pickup");

  // One delivery selection per shop group, matching the active tab; a still-
  // valid choice survives re-quotes.
  const [selections, setSelections] = useState<Record<string, DeliverySelection>>({});
  useEffect(() => {
    if (groups.length === 0) return;
    const m_2 = modeAvailable(mode) ? mode : modeAvailable("courier") ? "courier" : "pickup";
    if (m_2 !== mode) setMode(m_2);
    setSelections(prev => {
      const next: Record<string, DeliverySelection> = {};
      for (const g_0 of groups) {
        const existing = prev[g_0.shopId];
        const opt = existing ? g_0.options.find(o_0 => sameOption(o_0, existing)) : undefined;
        // Выбранная «Доставка продавцом» переживает смену вкладки: способ
        // выбирается на каждого продавца, а не общим переключателем.
        if (existing && opt && !opt.unavailable && (optionMode(opt) === m_2 || opt.carrier === "self")) {
          next[g_0.shopId] = opt.carrier === "self" ? toSelection(g_0.shopId, opt) : existing;
          continue;
        }
        const usable = g_0.options.filter(o_1 => !o_1.unavailable);
        const def_1 = usable.find(o_3 => optionMode(o_3) === m_2 && o_3.carrier !== "self") ?? usable.find(o_2 => optionMode(o_2) === m_2) ?? usable[0] ?? g_0.options[0];
        if (def_1) next[g_0.shopId] = toSelection(g_0.shopId, def_1);
      }
      return next;
    });
    // biome-ignore lint/correctness/useExhaustiveDependencies: react to a new quote landing or tab switch
  }, [quote.data, selfQ.data, pickupQ.data, mode]);
  const selectOption = (g_1: DeliveryQuoteGroup, o_4: DeliveryOption) => {
    if (o_4.unavailable) return;
    setSelections(prev_0 => {
      const existing_0 = prev_0[g_1.shopId];
      // Повторный выбор самовывоза не сбрасывает уже выбранную точку.
      if (existing_0 && sameOption(o_4, existing_0) && o_4.carrier === "seller_pickup") return prev_0;
      return {
        ...prev_0,
        [g_1.shopId]: toSelection(g_1.shopId, o_4)
      };
    });
  };
  // Точка самовывоза продавца: цена и адрес — из точки (шлюз пересчитает сам).
  const selectPickupPoint = (shopId: string, point: SellerPickupPoint) => setSelections(prev_1 => {
    const sel = prev_1[shopId];
    if (!sel || sel.carrier !== "seller_pickup") return prev_1;
    return {
      ...prev_1,
      [shopId]: {
        ...sel,
        pickupPointId: point.id,
        deliverySum: point.price,
        deliveryDays: point.readyLabel,
        pvzAddress: [point.name, point.city, point.address].filter(Boolean).join(", ")
      }
    };
  });
  // Одна доступная точка у магазина — подставляем её сразу, как только
  // выбран самовывоз; точек больше — покупатель выбирает сам.
  useEffect(() => {
    for (const [shopId_0, sel_0] of Object.entries(selections)) {
      if (sel_0.carrier !== "seller_pickup" || sel_0.pickupPointId) continue;
      const points = pickupOffers.get(shopId_0)?.points ?? [];
      if (points.length === 1 && points[0]) selectPickupPoint(shopId_0, points[0]);
    }
    // biome-ignore lint/correctness/useExhaustiveDependencies: реагируем на смену выбора и пришедшие точки
  }, [selections, pickupQ.data]);
  // Адрес выбранной точки самовывоза продавца — строка адреса и адрес заказа.
  const sellerPickupAddress = Object.values(selections).find(sel_1 => sel_1.carrier === "seller_pickup" && sel_1.pickupPointId)?.pvzAddress;
  // Во вкладке «В пункт выдачи» продавец может везти сам — тогда для его
  // группы нужен адрес «до двери», хотя остальные едут в ПВЗ.
  const selfInPickup = mode === "pickup" && Object.values(selections).some(sel_2 => sel_2.carrier === "self");
  const doorAddress = mode === "courier" || selfInPickup;
  // Адрес, введённый для своей доставки продавца, не переключает вкладку.
  const keepModeOnAddress = useRef(false);

  // Смена ПВЗ: сохраняем код/адрес во все СДЭК-группы «до пункта». Без
  // примерочной сбрасываем ранее выбранные примерку и частичный выкуп.
  const [pointHasDressingRoom, setPointHasDressingRoom] = useState(false);
  const setPickupPoint = (code_1: string, address: string, isDressingRoom: boolean) => {
    setPointHasDressingRoom(isDressingRoom);
    setSelections(prev_2 => {
      const next_0 = {
        ...prev_2
      };
      for (const [shopId_1, sel_3] of Object.entries(prev_2)) {
        if (!needsPickupPoint(sel_3)) continue;
        next_0[shopId_1] = {
          ...sel_3,
          pvzCode: code_1,
          pvzAddress: address,
          ...(isDressingRoom ? {} : {
            tryOn: false,
            partial: false
          })
        };
      }
      return next_0;
    });
  };
  const setSelectionExtras = (patch: {
    tryOn?: boolean;
    partial?: boolean;
  }) => setSelections(prev_3 => {
    const next_1 = {
      ...prev_3
    };
    for (const [shopId_2, sel_4] of Object.entries(prev_3)) if (needsPickupPoint(sel_4)) next_1[shopId_2] = {
      ...sel_4,
      ...patch
    };
    return next_1;
  });

  // Пункты выдачи перевозчика в городе назначения (для карты и проверки
  // доступности ранее выбранных пунктов).
  const needsPvz = Object.values(selections).some(needsPickupPoint);
  const pvzCarrier = Object.values(selections).find(needsPickupPoint)?.carrier ?? "sdek";
  const pointsQ = useQuery({
    queryKey: ["delivery", "points", dest.country, dest.city, dest.postalCode],
    queryFn: () => api.delivery.points({
      country: dest.country,
      city: dest.city,
      postalCode: dest.postalCode || undefined
    }),
    enabled: Boolean(dest.city) && (needsPvz || mode === "pickup")
  });
  const cityPoints = pointsQ.data ?? [];
  const selectedPvz = Object.values(selections).find(needsPickupPoint);

  // ── Суммы ──
  const liveItems = orderLines;
  const [done, setDone] = useState<{
    orderId: string;
    items: CartItem[];
    kind: "paid" | "invoice";
    email?: string;
    /** fd-promo-16: второй заказ раздельного оформления — его оплата отдельно. */
    next?: {
      id: string;
      number?: number;
      total: number;
    };
  } | null>(null);
  // Метрика: покупка уходит, когда заказ оформлен (экран «Заказ оформлен»),
  // по снимку заказа шлюза - с номером, суммой и позициями.
  const placedOrder = useRef<Parameters<typeof ecPurchaseFromOrder>[0] | null>(null);
  useEffect(() => {
    if (done && placedOrder.current?.id === done.orderId) ecPurchaseFromOrder(placedOrder.current);
  }, [done]);
  const items = liveItems.length > 0 ? liveItems : done?.items ?? [];
  const itemsCount = items.reduce((s_9, i_1) => s_9 + i_1.qty, 0);
  const subtotal = partial ? items.reduce((s_10, i_2) => s_10 + i_2.lineTotal, 0) : cart?.subtotal ?? items.reduce((s_11, i_3) => s_11 + i_3.lineTotal, 0);
  const discount = partial ? 0 : cart?.discount ?? 0;
  // Выбранных строк в корзине нет (оформили на другой вкладке, удалили) —
  // оформлять нечего, и молча подсовывать всю корзину нельзя: назад в корзину.
  const linesGone = partial && !done && cart != null && orderLines.length === 0;
  useEffect(() => {
    if (!linesGone) return;
    toast.error("Выбранных товаров уже нет в корзине");
    navigate("/cart", {
      replace: true
    });
  }, [linesGone, navigate]);
  // После оформления: полный заказ — корзину забываем (её удалит оплата),
  // частичный — перечитываем: шлюз убирает только оформленные строки, а
  // остальные товары ждут покупателя в корзине.
  const settleCart = () => {
    // Раздельное оформление: оба заказа частичные — корзина живёт до оплаты второго.
    if (partial || splitActive) void refreshCart();else clearCart();
  };
  // Код не на все позиции (fd-promo-15) — процент от всей суммы не рисуем.
  const discountPct = subtotal > 0 && items.every(i_4 => i_4.promoApplied) ? Math.round(discount / subtotal * 100) : 0;
  // fd-promo-05: код на бесплатную доставку — доставка этих магазинов 0
  // (перевозчику платит площадка). В частичный заказ код не переносится.
  const freeDeliveryShops = new Set(partial ? [] : cart?.freeDeliveryShopIds ?? []);
  const deliveryFull = groups.reduce((s_12, g_2) => s_12 + (selections[g_2.shopId]?.deliverySum ?? 0), 0);
  const deliveryTotal = groups.reduce((s_13, g_3) => s_13 + (freeDeliveryShops.has(g_3.shopId) ? 0 : selections[g_3.shopId]?.deliverySum ?? 0), 0);
  const freeDeliveryApplied = freeDeliveryShops.size > 0 && deliveryFull > deliveryTotal;
  const total = Math.max(0, subtotal - discount + deliveryTotal);
  // fd-promo-16: делить есть что — в корзине и товары под код, и без него.
  const canSplitPromo = !partial && Boolean(cart?.promoCode) && items.some(i_6 => i_6.promoApplied) && items.some(i_5 => !i_5.promoApplied);
  const splitActive = canSplitPromo && splitPromo && (payMethod === "card" || payMethod === "sbp") && !useBalance;
  const round2 = (v: number): number => Math.round(v * 100) / 100;
  const balanceAvailable = balanceQuery.data?.amount ?? 0;
  const canUseBalance = Boolean(buyerUser) && balanceAvailable > 0 && (payMethod === "card" || payMethod === "sbp");
  const requestedBalance = balanceInput.trim() === "" ? balanceAvailable : Number(balanceInput.replace(",", "."));
  const balanceApplied = canUseBalance && useBalance ? round2(Math.min(Number.isFinite(requestedBalance) && requestedBalance > 0 ? requestedBalance : 0, balanceAvailable, total)) : 0;
  const payableTotal = round2(total - balanceApplied);

  // ── Модалки ──
  type ModalKey = null | "recipients" | "recipientForm" | "addresses" | "addressEdit" | "pvzInfo" | "pvzList" | "addCard" | "invoice" | "invoicePreview" | "email";
  const [modal, setModal] = useState<ModalKey>(null);
  const [editingRecipient, setEditingRecipient] = useState<BuyerRecipient | null>(null);
  const [editingAddress, setEditingAddress] = useState<BuyerAddress | null>(null);
  const [picker, setPicker] = useState<PickerTab | null>(null);
  const [pickerCourier, setPickerCourier] = useState<CourierAddressValues>(emptyCourierAddress);
  const [courierBusy, setCourierBusy] = useState(false);
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const emailResolve = useRef<((email: string | null) => void) | null>(null);

  // Имя и телефон, которые сохраняются вместе с адресом. В адресе они
  // необязательны: получатель выбирается в оформлении отдельно (кадры
  // «Получатель» / «Добавить курьерский адрес»). Раньше у покупателя без
  // имени в профиле сюда уходила пустая строка, и «Привезти сюда» падало с
  // английской ошибкой шлюза (Айталы 02.10.2026, A-32).
  const contactFor = (): {
    recipient: string;
    phone: string;
  } => {
    const recipientName = recipient ? [recipient.firstName, recipient.lastName].filter(Boolean).join(" ") : "";
    return {
      recipient: recipientName || buyerProfile?.name || buyerUser?.name || "",
      phone: recipient?.phone || buyerProfile?.phone || buyerUser?.phone || ""
    };
  };
  const pickPoint = (p_0: PickupPoint | SavedPickupPoint) => {
    setPickupPoint(p_0.code, p_0.address, p_0.isDressingRoom);
    if (buyerUser) void api.buyer.savePickupPoint({
      carrier: pvzCarrier,
      code: p_0.code,
      name: p_0.name,
      address: p_0.address,
      city: p_0.city,
      workTime: p_0.workTime,
      isDressingRoom: p_0.isDressingRoom,
      latitude: p_0.latitude,
      longitude: p_0.longitude
    }).then(() => savedPointsQ.refetch()).catch(() => undefined);
  };
  const openAddressRow = () => {
    if (mode === "pickup") {
      if (!needsPvz) {
        setPicker("pickup");
        return;
      }
      if (selectedPvz?.pvzCode) setModal("pvzInfo");else if (savedPoints.length > 0) setModal("pvzList");else setPicker("pickup");
      return;
    }
    if (buyerUser && addresses.length > 0) setModal("addresses");else {
      setPickerCourier(courier);
      setPicker("courier");
    }
  };
  const submitPickerCourier = async () => {
    const input = courierAddressInput(pickerCourier, contactFor(), addresses.length === 0);
    if (!input.city || !input.street) {
      toast.error("Укажите город, улицу и дом через запятую");
      return;
    }
    setCourierBusy(true);
    try {
      if (buyerUser) {
        const saved = await api.buyer.createAddress(input);
        await addressesQ.refetch();
        applyAddress(saved);
      } else {
        setCourier(pickerCourier);
        setAddressId(null);
        setDest({
          country: input.country,
          city: input.city,
          postalCode: input.postalCode ?? ""
        });
        destTouched.current = true;
      }
      if (!keepModeOnAddress.current) setMode("courier");
      keepModeOnAddress.current = false;
      setPicker(null);
    } catch (e_0) {
      toast.error(e_0 instanceof Error ? e_0.message : "Не удалось сохранить адрес");
    } finally {
      setCourierBusy(false);
    }
  };

  // Почта обязательна для чека/счёта: берём из профиля, иначе спрашиваем
  // модалкой «Указать электронную почту» и сохраняем в кабинете.
  const ensureEmail = (): Promise<string | null> => {
    const known = buyerProfile?.email?.trim();
    if (known) return Promise.resolve(known);
    return new Promise(resolve => {
      emailResolve.current = resolve;
      setModal("email");
    });
  };
  if (items.length === 0) {
    return <EmptyState icon={ShoppingBag} title={t("cart.empty")} action={<Link to="/catalog" className="inline-flex h-10 items-center rounded-[24px] bg-osmix-tangelo px-6 type-button text-white outline-none transition-colors hover:bg-osmix-tangelo/90 focus-visible:ring-2 focus-visible:ring-osmix-tangelo/40">
						{t("cart.continueShopping")}
					</Link>} />;
  }

  // The widget's onSuccess is unreliable, so after the order is created we
  // POLL `confirm`: it keeps the order pending until the PSP actually has the
  // charge, then finalizes it. Stops on canonical `new`, a terminal decline,
  // or a timeout (the webhook is the server-side backstop).
  const startPolling = (orderId: string, snapshot: CartItem[], next_2?: {
    id: string;
    number?: number;
    total: number;
  }) => {
    if (pollRef.current) pollRef.current.cancelled = true;
    const token_0 = {
      cancelled: false
    };
    pollRef.current = token_0;
    const deadline = Date.now() + 4 * 60 * 1000;
    const tick = async () => {
      if (token_0.cancelled) return;
      try {
        const o_5 = await api.orders.confirm(orderId);
        if (token_0.cancelled) return;
        // Индивидуальный заказ после оплаты сразу «Подтверждён» (fd-order-29).
        if (isPaidOrderStatus(o_5.status)) {
          reachGoalOnce(METRIKA_GOALS.payment, o_5.id);
          setDone({
            orderId: o_5.id,
            items: snapshot,
            kind: "paid",
            ...(next_2 ? {
              next: next_2
            } : {})
          });
          settleCart();
          setSubmitting(false);
          return;
        }
        if (o_5.status === "declined") {
          setSubmitting(false);
          toast.error(t("checkout.declined"));
          return;
        }
      } catch {
        // transient — retry
      }
      if (token_0.cancelled) return;
      if (Date.now() > deadline) {
        setSubmitting(false);
        toast.message(t("checkout.paymentProcessing"));
        return;
      }
      setTimeout(tick, 2500);
    };
    void tick();
  };
  const goToOrder = (orderId_0: string) => navigate(buyerUser ? `/profile/orders/${encodeURIComponent(orderId_0)}` : `/checkout/success?order=${encodeURIComponent(orderId_0)}`);
  const onSubmit = async () => {
    if (!cart || done) return;
    if (!dest.city || groups.length === 0) {
      toast.error(t("checkout.enterAddressForDelivery"));
      openAddressRow();
      return;
    }
    const selected: DeliverySelection[] = [];
    for (const g_4 of groups) {
      const sel_5 = selections[g_4.shopId];
      if (!sel_5) {
        toast.error(t("checkout.selectDelivery"));
        return;
      }
      if (needsPickupPoint(sel_5) && !sel_5.pvzCode) {
        toast.error(t("checkout.selectPickupPoint"));
        openAddressRow();
        return;
      }
      if (sel_5.carrier === "seller_pickup" && !sel_5.pickupPointId) {
        toast.error(groups.length > 1 ? `${g_4.shopName}: выберите точку самовывоза` : "Выберите точку самовывоза");
        return;
      }
      selected.push(sel_5);
    }
    if (doorAddress && !courier.street && !courier.line) {
      toast.error(t("checkout.enterAddressForDelivery"));
      openAddressRow();
      return;
    }
    if (!recipient?.phone || !recipient.firstName) {
      toast.error("Укажите получателя");
      setModal(recipients.length > 0 ? "recipients" : "recipientForm");
      return;
    }
    if (payMethod === "invoice" && !billing) {
      setModal("invoice");
      return;
    }
    const email = await ensureEmail();
    if (!email) return;
    setSubmitting(true);
    const snapshot_0 = [...liveItems];
    try {
      // Курьеру — улица с квартирой; этаж/подъезд/домофон и комментарий —
      // в комментарии к адресу (заказ хранит адрес строками).
      const courierExtra = [courier.floor && `этаж ${courier.floor}`, courier.entrance && `подъезд ${courier.entrance}`, courier.intercom && `домофон ${courier.intercom}`, courier.comment].filter(Boolean).join(", ");
      const street = doorAddress ? [courierAddressInput(courier, contactFor()).street, courier.apartment && `кв. ${courier.apartment}`].filter(Boolean).join(", ") : selectedPvz?.pvzAddress || sellerPickupAddress || courier.street || dest.city;
      const discoverySource = currentDiscoverySource();
      // Цифровой UGC-артикул с устройства (30 дней): заказ засчитается ему.
      const ugcArticleCode = readUgcArticleCode();
      const req: CreateOrderRequest = {
        cartId: cart.id,
        contact: {
          name: [recipient.firstName, recipient.lastName].filter(Boolean).join(" "),
          email,
          phone: recipient.phone
        },
        address: {
          country: dest.country,
          city: dest.city,
          street,
          postalCode: doorAddress ? courier.postalCode || dest.postalCode : dest.postalCode,
          comment: doorAddress ? courierExtra || undefined : undefined,
          ...(dest.region ? {
            region: dest.region
          } : {})
        },
        selections: selected,
        ...(payMethod === "invoice" ? {
          paymentMethod: "invoice" as const
        } : {}),
        ...(payMethod === "cod" ? {
          paymentMode: "cod" as const
        } : {}),
        ...((payMethod === "card" || payMethod === "sbp") && useBalance && balanceApplied > 0 ? {
          useBalance: true,
          balanceAmount: balanceApplied
        } : {}),
        // «Как покупатель нашёл товар» для кабинета продавца. Значение
        // запомнено на переходе к карточке товара, иногда несколько дней
        // назад; здесь его только достают. Не запомнили — поля нет, и
        // кабинет честно показывает «—».
        ...(discoverySource ? {
          discoverySource
        } : {}),
        ...(ugcArticleCode ? {
          ugcArticleCode
        } : {}),
        ...(lineIds ? {
          lineIds
        } : {}),
        ...(splitActive ? {
          splitPromo: true
        } : {}),
        ...(savedCardsOn && payKey === "card:new" && saveCard ? {
          saveCard: true
        } : {})
      };
      const order = await api.orders.createPending(req);
      // fd-promo-16: оформлено два заказа — сейчас платим первый (со скидкой),
      // второй оплачивается отдельно по своей ссылке (/pay/:id).
      const nextOrder = order.checkoutOrders?.find(o_6 => o_6.id !== order.id);
      const paySnapshot = nextOrder ? snapshot_0.filter(i_7 => i_7.promoApplied) : snapshot_0;
      placedOrder.current = order;
      reachGoal(METRIKA_GOALS.checkout, {
        order_id: order.id
      });
      // Шаг «заказ» воронки идей: товары, открытые из идеи (fd-discovery-06).
      trackOrderCreated(order.id, order.groups.flatMap(g_5 => g_5.items.map(i_8 => i_8.productId)));
      // Источник относится к оформленному заказу: следующий покупает заново.
      clearDiscoverySource();
      if (payMethod === "invoice") {
        setDone({
          orderId: order.id,
          items: snapshot_0,
          kind: "invoice",
          email
        });
        settleCart();
        setSubmitting(false);
        setInvoice(null);
        setInvoiceLoading(true);
        setModal("invoicePreview");
        try {
          setInvoice(await api.buyer.getOrderInvoice(order.id));
        } catch (e_2) {
          toast.error(e_2 instanceof Error ? e_2.message : "Счёт пока не готов — он появится в заказе");
        } finally {
          setInvoiceLoading(false);
        }
        return;
      }
      if (payMethod === "cod") {
        setDone({
          orderId: order.id,
          items: snapshot_0,
          kind: "paid"
        });
        settleCart();
        setSubmitting(false);
        return;
      }
      const cfg = await api.payments.config();
      const remainingToCharge = order.payableTotal ?? order.total;
      // fd-payment-01: оплата сохранённой картой — без ввода номера. Отказ
      // не теряет заказ: дальше обычная оплата картой того же заказа.
      if (savedCardId && remainingToCharge > 0 && cfg.savedCards) {
        const paid = await api.orders.paySavedCard(order.id, savedCardId).catch((e_3: unknown) => ({
          status: "declined" as const,
          reason: e_3 instanceof Error ? e_3.message : t("checkout.declined")
        }));
        if (paid.status === "paid") {
          startPolling(order.id, paySnapshot, nextOrder);
          return;
        }
        if (paid.status === "redirect") {
          window.location.assign(paid.url);
          return;
        }
        if (paid.status === "3ds") {
          submitAcsForm(paid);
          return;
        }
        toast.error(`Сохранённая карта не прошла: ${paid.reason}. Оплатите картой заново.`);
      }
      if (cfg.provider === "mock" || remainingToCharge <= 0) {
        startPolling(order.id, paySnapshot, nextOrder);
        return;
      }
      // Робокасса платит редиректом: уводим покупателя на её страницу, а
      // корзину чистим уже по возвращении — если он передумает прямо там,
      // заказ останется ожидающим оплаты и его можно будет открыть снова.
      if (cfg.mode === "redirect") {
        const {
          url
        } = await api.payments.url(order.id);
        window.location.assign(url);
        return;
      }
      const cp = await loadCloudPayments();
      new cp.CloudPayments().pay("charge", {
        publicId: cfg.publicId ?? "",
        description: t("checkout.orderDescription", {
          id: order.id
        }),
        amount: remainingToCharge,
        currency: cfg.currency,
        invoiceId: order.id,
        accountId: email,
        skin: "mini",
        data: {
          orderId: order.id
        },
        // fd-payment-01: сохранить карту по согласию покупателя (учитывается,
        // когда в кабинете CloudPayments включено «Спрашивать пользователя»).
        ...(savedCardsOn && saveCard ? {
          tokenize: true
        } : {})
      }, {
        onSuccess: () => startPolling(order.id, paySnapshot, nextOrder),
        onFail: reason => {
          if (pollRef.current) pollRef.current.cancelled = true;
          setSubmitting(false);
          toast.error(reason || t("checkout.declined"));
        }
      });
      startPolling(order.id, paySnapshot, nextOrder);
    } catch (e_1) {
      toast.error(e_1 instanceof Error ? e_1.message : t("common.error"));
      setSubmitting(false);
    }
  };

  // Подписи вкладок доставки: срок и стоимость лучшего варианта вкладки.
  const tabMeta = (m_3: Mode): string[] => {
    if (!dest.city || groups.length === 0) return [];
    const opts = groups.map(g_6 => {
      const sel_6 = selections[g_6.shopId];
      const chosen = sel_6 && mode === m_3 ? g_6.options.find(o_7 => sameOption(o_7, sel_6)) : undefined;
      return chosen ?? g_6.options.filter(o_8 => optionMode(o_8) === m_3 && !o_8.unavailable).sort((a_1, b) => a_1.deliverySum - b.deliverySum)[0];
    });
    if (opts.some(o_9 => !o_9)) return [];
    const sum = opts.reduce((s_14, o_10) => s_14 + (o_10?.deliverySum ?? 0), 0);
    const days = opts.find(o_11 => o_11?.deliveryDays)?.deliveryDays;
    return [days, fmt(sum)].filter((v_0): v_0 is string => Boolean(v_0));
  };
  const addressRowText = (() => {
    if (!dest.city) return mode === "pickup" ? "Выберите пункт выдачи" : "Укажите адрес доставки";
    if (mode === "pickup") {
      if (needsPvz) return selectedPvz?.pvzAddress ? `${carrierTitle(pvzCarrier)},  ${selectedPvz.pvzAddress}` : `Выберите пункт выдачи в городе ${dest.city}`;
      if (sellerPickupAddress) return `${SELLER_PICKUP_LABEL},  ${sellerPickupAddress}`;
      // Строка пункта выдачи — только по группам, которые едут в пункт:
      // «Доставка продавцом» у соседней группы идёт своей строкой ниже.
      const label = groups.map(g_7 => selectedOptionFor(g_7, selections[g_7.shopId])).filter(o_12 => o_12?.toPickupPoint).map(o_13 => o_13?.label)[0];
      return [label ?? "Самовывоз", dest.city].join(",  ");
    }
    const line = courier.street ? [dest.city, courierAddressLabel({
      street: courier.street,
      apartment: courier.apartment,
      floor: ""
    })].join(",  ") : "";
    return line || `${dest.city}: укажите улицу и дом`;
  })();
  const courierGroups = mode === "courier" ? groups : [];
  const pvzOption = groups.map(g_8 => selectedOptionFor(g_8, selections[g_8.shopId])).find(o_14 => o_14?.toPickupPoint);
  const showTryOn = Boolean(selectedPvz?.pvzCode) && pointHasDressingRoom && groups.some(g_9 => selectedOptionFor(g_9, selections[g_9.shopId])?.supportsTryOn);
  const showPartial = Boolean(selectedPvz?.pvzCode) && pointHasDressingRoom && groups.some(g_10 => selectedOptionFor(g_10, selections[g_10.shopId])?.supportsPartial);
  const shopNames = groups.map(g_11 => g_11.shopName);
  // Условия продавца под его вариантом «Доставка продавцом»: заметка из
  // кабинета («Условия для покупателя») и сколько осталось до «бесплатно от».
  const groupSubtotal = (shopId_3: string) => liveItems.filter(i_9 => i_9.shopId === shopId_3).reduce((s_15, i_10) => s_15 + i_10.lineTotal, 0);
  const selfTerms = (group: DeliveryQuoteGroup) => {
    const offer = selfOffers.get(group.shopId);
    if (!offer) return null;
    const left = selfFreeOverLeft(offer, groupSubtotal(group.shopId));
    const note = offer.note?.trim();
    if (!note && left == null && !(offer.freeOver && offer.price === 0)) return null;
    return <div className="mt-2 flex flex-col gap-0.5 text-[12px] leading-4 text-[#1F1F1F]/64" data-testid="self-delivery-terms">
				{left != null && offer.freeOver != null && <span>
						{SELF_DELIVERY_LABEL}: бесплатно от {fmt(offer.freeOver)} — добавьте товаров ещё на {fmt(left)}
					</span>}
				{left == null && offer.freeOver != null && offer.price === 0 && <span>{SELF_DELIVERY_LABEL}: бесплатно — заказ от {fmt(offer.freeOver)}</span>}
				{note && <span>Условия продавца: {note}</span>}
			</div>;
  };

  // Самовывоз от продавца под вариантом группы: список точек с выбором (адрес,
  // график, срок готовности, стоимость) или, если в городе покупателя точек
  // нет, — подсказка с городами точек и переход на такой город.
  const sellerPickupBlock = (group_0: DeliveryQuoteGroup) => {
    const offer_0 = pickupOffers.get(group_0.shopId);
    if (!offer_0) return null;
    const sel_7 = selections[group_0.shopId];
    if (!offer_0.available) {
      return <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-[12px] leading-4 text-[#1F1F1F]/64" data-testid="seller-pickup-other-city">
					<span>
						{SELLER_PICKUP_LABEL} — в {offer_0.cities.map(c => `г. ${c}`).join(", ")}.
					</span>
					{offer_0.cities.map(c_0 => <button key={c_0} type="button" onClick={() => {
          destTouched.current = true;
          setDest({
            country: dest.country,
            city: c_0,
            postalCode: ""
          });
        }} className="rounded-sm font-medium text-[#1F1F1F] underline underline-offset-2 outline-none focus-visible:ring-2 focus-visible:ring-osmix-tangelo/40">
							Выбрать г. {c_0}
						</button>)}
				</div>;
    }
    if (sel_7?.carrier !== "seller_pickup") return null;
    return <div className="mt-3 flex flex-col gap-2" role="radiogroup" aria-label="Точка самовывоза" data-testid="seller-pickup-points">
				{offer_0.points.map(p_1 => {
        const active = sel_7.pickupPointId === p_1.id;
        return <button key={p_1.id} type="button" role="radio" aria-checked={active} data-testid="seller-pickup-point" onClick={() => selectPickupPoint(group_0.shopId, p_1)} className={cn("flex w-full items-start gap-3 rounded-[12px] border bg-white p-3 text-left outline-none transition-colors focus-visible:ring-2 focus-visible:ring-osmix-tangelo/40", active ? "border-[#FA5C2E]" : "border-transparent hover:border-[#1F1F1F]/12")}>
							<span aria-hidden="true" className={cn("mt-0.5 grid size-5 shrink-0 place-items-center rounded-full border", active ? "border-transparent bg-[#FA5C2E]" : "border-[#1F1F1F]/24 bg-white")}>
								{active && <span className="size-2 rounded-full bg-white" />}
							</span>
							<span className="flex min-w-0 flex-1 flex-col gap-0.5">
								<span className="flex flex-wrap items-baseline justify-between gap-x-3">
									<span className="text-[14px] leading-[18px] font-semibold text-[#161616]">{p_1.name}</span>
									<span className={cn("text-[14px] leading-[18px] font-semibold", p_1.price === 0 ? "text-ds-success" : "text-[#161616]")}>
										{p_1.price === 0 ? "Бесплатно" : fmt(p_1.price)}
									</span>
								</span>
								<span className="text-[14px] leading-[18px] text-[#1F1F1F]">{[p_1.inBuyerCity ? "" : `г. ${p_1.city}`, p_1.address].filter(Boolean).join(", ")}</span>
								<span className="text-[12px] leading-4 text-[#1F1F1F]/64">
									{[p_1.schedule, `готов ${p_1.readyLabel}`, `хранится ${p_1.storageDays} дн.`].filter(Boolean).join(" · ")}
								</span>
								{active && p_1.instructions ? <span className="mt-1 text-[12px] leading-4 text-[#1F1F1F]/64">{p_1.instructions}</span> : null}
							</span>
						</button>;
      })}
			</div>;
  };
  const submitLabel = submitting ? t("checkout.processing") : payMethod === "invoice" ? "Сформировать счёт" : payMethod === "cod" ? t("checkout.codPlaceOrder", {
    defaultValue: "Оформить заказ"
  }) : t("checkout.pay", {
    defaultValue: "Оплатить"
  });
  return <div className="mx-auto w-full max-w-[1140px]">
			{/* Хлебные крошки 20/23 w600 #2E2E2E, шеврон 24 через 10: «Корзина › Оформление заказа» (3919:17413) */}
			<nav className="flex flex-wrap items-center gap-2.5 text-[20px] leading-[23px] font-semibold tracking-[-0.31px] text-[#2E2E2E]" aria-label={t("checkout.title")}>
				<Link to="/cart" className="rounded-sm outline-none transition-colors hover:text-osmix-tangelo focus-visible:ring-2 focus-visible:ring-osmix-tangelo/40">
					{t("checkout.breadcrumbCart", {
          defaultValue: "Корзина"
        })}
				</Link>
				<ChevronRightStrokeIcon size={24} className="shrink-0" />
				<span>{t("checkout.title")}</span>
			</nav>

			<div className="mt-5 grid items-start gap-5 lg:grid-cols-[minmax(0,753px)_367px] lg:justify-between">
				<div className="flex min-w-0 flex-col">
					{/* Доставка: заголовок 16/20 w600, превью 77×102 r4 через 10 */}
					<h2 className="text-[16px] leading-5 font-semibold tracking-[-0.31px] text-[#161616]">Доставка</h2>
					<div className="mt-3 flex gap-2.5 overflow-x-auto [scrollbar-width:none]">
						{items.map(i_11 => {
            // fd-promo-15: позиции, к которым применён промокод, — с пометкой и суммой скидки.
            const promoLine = !partial && cart?.promoCode && i_11.promoApplied;
            return <div key={i_11.id} className="relative h-[102px] w-[77px] shrink-0 overflow-hidden rounded-[4px] bg-white" title={[`${i_11.name} · ${i_11.qty} × ${fmt(i_11.unitPrice)}`, promoLine && i_11.discount ? `промокод −${fmt(i_11.discount)}` : ""].filter(Boolean).join(", ")}>
									{i_11.photo && <img src={i_11.photo} alt={i_11.name} className="size-full object-cover" />}
									{promoLine && <span className="absolute inset-x-0 bottom-0 bg-[#FA5C2E] px-1 py-0.5 text-center text-[10px] leading-3 font-semibold text-white" data-testid="checkout-line-promo">
											{i_11.discount && i_11.discount > 0 ? `−${fmt(i_11.discount)}` : cart?.promoKind === "free_delivery" ? "доставка 0" : cart?.promoCode}
										</span>}
								</div>;
          })}
					</div>

					{/* Карточка доставки #F8F8F8 r12, отступ 10: вкладки 16/20 w600 (неактивная 48 %),
         под ними «срок  цена» 12/16, линия #1F1F1F 8 %; строка адреса 14/18; чипы. */}
					<section className="mt-3 flex flex-col rounded-[12px] bg-[#F8F8F8] p-2.5" data-testid="delivery-card">
						<div className="flex w-full max-w-[285px] gap-6 border-b border-[#1F1F1F]/8" role="tablist">
							{([["pickup", "В пункт выдачи"], ["courier", "Курьером"]] as const).map(([key, label_0]) => {
              const enabled = !dest.city || groups.length === 0 || modeAvailable(key);
              return <button key={key} type="button" role="tab" aria-selected={mode === key} disabled={!enabled} data-testid={`delivery-tab-${key}`} onClick={() => setMode(key)} className={cn("-mb-px flex flex-col items-start gap-0.5 border-b pt-1.5 pb-2.5 text-left outline-none focus-visible:ring-2 focus-visible:ring-osmix-tangelo/40 disabled:opacity-40", mode === key ? "border-[#1F1F1F]" : "border-transparent")}>
										<span className={cn("text-[16px] leading-5 font-semibold tracking-[-0.31px] whitespace-nowrap", mode === key ? "text-[#1F1F1F]" : "text-[#1F1F1F]/48")}>{label_0}</span>
										<span className={cn("flex min-h-4 gap-1.5 text-[12px] leading-4", mode === key ? "text-black" : "text-[#1F1F1F]/48")}>
											{tabMeta(key).map(part => <span key={part}>{part}</span>)}
										</span>
									</button>;
            })}
						</div>

						<button type="button" onClick={openAddressRow} data-testid="delivery-address-row" className="mt-2.5 flex max-w-full items-center gap-2 self-start rounded-[6px] py-2 text-left text-[14px] leading-[18px] whitespace-pre text-black outline-none focus-visible:ring-2 focus-visible:ring-osmix-tangelo/40">
							<span className="truncate">{quote.isFetching && dest.city && groups.length === 0 ? t("checkout.deliveryLoading") : addressRowText}</span>
							<ChevronRight16Icon size={16} className="shrink-0" />
						</button>

						{/* Курьер: тарифы группы — чипы h32 с бейджем цены (кадр 3924:17790). */}
						{courierGroups.map(g_12 => <div key={g_12.shopId} className="mt-2.5">
								{groups.length > 1 && <p className="mb-2 text-[12px] leading-4 text-[#1F1F1F]/64">{g_12.shopName}</p>}
								<div className="flex flex-wrap gap-2.5">
									{g_12.options.filter(o_15 => optionMode(o_15) === "courier").map(o_16 => {
                const sel_8 = selections[g_12.shopId];
                const active_0 = Boolean(sel_8 && sameOption(o_16, sel_8));
                return <DeliveryChip key={`${o_16.carrier}:${o_16.tariffCode ?? o_16.method ?? ""}`} testId={o_16.carrier === "self" ? "delivery-option-self" : "delivery-option"} active={active_0} disabled={o_16.unavailable} title={o_16.unavailable ? o_16.unavailableReason : undefined} onClick={() => selectOption(g_12, o_16)} label={[o_16.label, o_16.deliveryDays].filter(Boolean).join(", ")} price={fmt(o_16.deliverySum)} free={o_16.deliverySum === 0} />;
              })}
								</div>
								{selfTerms(g_12)}
							</div>)}

						{/* Самовывоз с несколькими вариантами (например, СДЭК и магазин). */}
						{mode === "pickup" && groups.map(g_13 => {
            const self = g_13.options.find(o_17 => o_17.carrier === "self");
            const pickupOpts = g_13.options.filter(o_18 => optionMode(o_18) === "pickup");
            if (pickupOpts.length < 2 && !self) return null;
            const opts_0 = self ? [...pickupOpts, self] : pickupOpts;
            const sel_9 = selections[g_13.shopId];
            return <div key={g_13.shopId} className="mt-2.5">
										{groups.length > 1 && <p className="mb-2 text-[12px] leading-4 text-[#1F1F1F]/64">{g_13.shopName}</p>}
										<div className="flex flex-wrap gap-2.5">
										{opts_0.map(o_19 => {
                  const active_1 = Boolean(sel_9 && sameOption(o_19, sel_9));
                  return <DeliveryChip key={`${o_19.carrier}:${o_19.tariffCode ?? o_19.method ?? ""}`} testId={o_19.carrier === "self" ? "delivery-option-self" : o_19.carrier === "seller_pickup" ? "delivery-option-seller-pickup" : "delivery-option"} active={active_1} disabled={o_19.unavailable} title={o_19.unavailable ? o_19.unavailableReason : undefined} onClick={() => selectOption(g_13, o_19)} label={[o_19.label, o_19.carrier === "self" || o_19.carrier === "seller_pickup" ? o_19.deliveryDays : undefined].filter(Boolean).join(", ")} price={fmt(o_19.deliverySum)} free={o_19.deliverySum === 0} />;
                })}
										</div>
										{selfTerms(g_13)}
										{sellerPickupBlock(g_13)}
									</div>;
          })}

						{/* Продавец везёт сам, остальные — в пункт выдачи: адрес «до двери»
          для его группы отдельной строкой, вкладка не переключается. */}
						{selfInPickup && <button type="button" data-testid="self-delivery-address-row" onClick={() => {
            keepModeOnAddress.current = true;
            if (buyerUser && addresses.length > 0) setModal("addresses");else {
              setPickerCourier(courier);
              setPicker("courier");
            }
          }} className="mt-3 flex max-w-full items-center gap-2 rounded-[6px] text-left text-[14px] leading-5 whitespace-pre text-[#1F1F1F] outline-none focus-visible:ring-2 focus-visible:ring-osmix-tangelo/40">
								<span className="truncate">
									{courier.street ? `${SELF_DELIVERY_LABEL}:  ${[dest.city, courierAddressLabel({
                street: courier.street,
                apartment: courier.apartment,
                floor: ""
              })].join(",  ")}` : `${SELF_DELIVERY_LABEL}: укажите улицу и дом`}
								</span>
								<ChevronRight16Icon size={16} className="shrink-0" />
							</button>}

						{(showTryOn || showPartial) && <div className="mt-3 flex flex-col gap-2.5">
								{showTryOn && <CheckRow checked={Boolean(selectedPvz?.tryOn)} onChange={tryOn => setSelectionExtras({
              tryOn
            })} className="items-center" testId="delivery-try-on">
										Примерка на пункте выдачи
									</CheckRow>}
								{showPartial && <CheckRow checked={Boolean(selectedPvz?.partial)} onChange={partial_0 => setSelectionExtras({
              partial: partial_0
            })} className="items-center" testId="delivery-partial">
										Частичный выкуп — забрать только подошедшее
									</CheckRow>}
							</div>}
					</section>

					{/* Получатель: #F8F8F8 r16, отступ 10, заголовок 16/20 w600, ниже через 10
         строка «Имя Фамилия,  телефон» 14/18 и шеврон 24 справа (3919:17413). */}
					<button type="button" onClick={() => buyerUser && recipients.length > 0 ? setModal("recipients") : (setEditingRecipient(null), setModal("recipientForm"))} data-testid="recipient-card" className="mt-3 flex flex-col gap-2.5 rounded-[16px] bg-[#F8F8F8] p-2.5 text-left outline-none focus-visible:ring-2 focus-visible:ring-osmix-tangelo/40">
						<span className="text-[16px] leading-5 font-semibold tracking-[-0.31px] text-[#1F1F1F]">{t("checkout.recipient", {
              defaultValue: "Получатель"
            })}</span>
						<span className="flex min-h-6 w-full items-center justify-between gap-3">
							<span className="min-w-0 truncate text-[14px] leading-[18px] whitespace-pre text-black">
								{recipient?.firstName ? `${[recipient.firstName, recipient.lastName].filter(Boolean).join(" ")},  ${phoneLabel(recipient.phone)}` : "Добавьте получателя"}
							</span>
							<ChevronRightStrokeIcon size={24} className="shrink-0 text-[#1E1E1E]" />
						</span>
					</button>

					{/* Способ оплаты: через 20 после получателя, заголовок 16/20 w600, плитки через 12 */}
					<h2 className="mt-5 text-[16px] leading-5 font-semibold tracking-[-0.31px] text-[#161616]">Способ оплаты</h2>
					<div className="mt-3 flex gap-2.5 overflow-x-auto pb-1 [scrollbar-width:none]" data-testid="pay-methods">
						{methods.map(m_4 => {
            const key_0: PayKey = m_4.provider === "sbp" ? `sbp:${m_4.id}` : `card:${m_4.id}`;
            return <PayTile key={m_4.id} selected={payKey === key_0} onClick={() => setPayKey(key_0)} icon={m_4.provider === "sbp" ? <SbpIcons /> : <BankIcon />} label={m_4.provider === "sbp" ? `СБП • ${m_4.brand || m_4.label}` : `•• ${m_4.last4}`} />;
          })}
						{/* Карта вводится только на платёжной странице банка (требование эквайера):
          своей формы ввода номера и CVC на сайте нет. */}
						<PayTile selected={payKey === "card:new"} onClick={() => setPayKey("card:new")} icon={<BankIcon />} label="Картой онлайн" />
						<PayTile selected={payKey === "sbp"} onClick={() => setPayKey("sbp")} icon={<SbpIcons single />} label="СБП" testId="pay-method-sbp" />
						{buyerUser && <PayTile selected={payKey === "invoice"} onClick={() => billing ? setPayKey("invoice") : setModal("invoice")} icon={<IconCircle><WorkIcon size={16} /></IconCircle>} label={billing ? billing.companyName : "Счёт для юрлиц"} testId="pay-method-invoice" />}
						{COD_ENABLED && <PayTile selected={payKey === "cod"} onClick={() => setPayKey("cod")} icon={<IconCircle><Landmark className="size-4" /></IconCircle>} label="При получении" testId="pay-method-cod" />}
					</div>
					{payKey === "invoice" && billing && <button type="button" onClick={() => setModal("invoice")} className="mt-2 self-start text-[12px] text-[#1F1F1F]/64 underline underline-offset-2">
							Изменить реквизиты
						</button>}

					{/* fd-payment-01: согласие сохранить карту — при оплате новой картой.
         Номер карты вводится на странице платёжной системы, сюда
         возвращаются только токен и маска. */}
					{savedCardsOn && payKey === "card:new" && <div className="mt-4 flex flex-col gap-1 text-[14px] text-[#1F1F1F]">
							<CheckRow checked={saveCard} onChange={setSaveCard} className="items-center" testId="save-card">
								Сохранить карту
							</CheckRow>
							<span className="text-[12px] text-[#1F1F1F]/48">
								Следующий заказ оплатите без ввода номера. Храним только маску и токен платёжной системы.
							</span>
						</div>}

					{/* fd-payment-03: списание с внутреннего баланса (карта/СБП). */}
					{canUseBalance && <div className="mt-4 flex flex-wrap items-center gap-3 text-[14px] text-[#1F1F1F]">
							<CheckRow checked={useBalance} onChange={setUseBalance} className="items-center" testId="use-balance">
								{t("checkout.useBalance")}
							</CheckRow>
							<span className="text-[12px] text-[#1F1F1F]/48">{t("checkout.balanceAvailable", {
              amount: fmt(balanceAvailable)
            })}</span>
							{useBalance && <Input inputMode="decimal" value={balanceInput} placeholder={String(Math.min(balanceAvailable, total))} onChange={e_4 => setBalanceInput(e_4.target.value)} data-testid="balance-amount-input" fieldClassName="h-8 w-28" />}
						</div>}
				</div>

				{/* Сводка 367 #F8F8F8 r16, отступ 10, промежутки 16 (3919:17413): «N товара»,
        «Скидка OSMIX», «Доставка» через 10, промокод, «Итого». Над ней после счёта —
        зелёная плашка «Мы отправим счёт на вашу почту» (3927:23578). */}
				<aside className="flex flex-col lg:sticky lg:top-28">
					{done?.kind === "invoice" && <div className="flex items-start gap-2.5 rounded-t-[16px] bg-[#A9DCA8] px-4 py-2 text-[16px] leading-[22px] text-white" data-testid="invoice-banner">
							<svg viewBox="0 0 24 24" className="mt-0.5 size-6 shrink-0" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden="true">
								<path d="m4 12 5 5L20 6" />
							</svg>
							<span className="min-w-0 break-words">
								{done.email ? <>
										Мы отправим счёт на вашу почту <span className="block">{done.email}</span>
									</> : "Счёт сформирован и сохранён в заказе"}
							</span>
						</div>}
					<div className={cn("flex flex-col gap-4 bg-[#F8F8F8] p-2.5", done?.kind === "invoice" ? "rounded-b-[16px]" : "rounded-[16px]")}>
						<div className="flex flex-col gap-2.5">
							<SummaryRow label={t("cart.itemsCount", {
              defaultValue: "{{count}} товара",
              count: itemsCount
            })} value={fmt(subtotal)} />
							{discount > 0 && <div className="flex items-center justify-between gap-1 text-[#FA5C2E]" data-testid="osmix-discount-row">
									<span className="text-[14px] leading-[15px] font-semibold tracking-[-0.28px]">
										{cart?.promoShopId ? t("checkout.shopDiscount", {
                  defaultValue: "Скидка магазина"
                }) : t("checkout.osmixDiscount", {
                  defaultValue: "Скидка OSMIX"
                })}
									</span>
									<span className="flex items-baseline gap-1">
										<span className="text-[14px] leading-[19px] font-bold tracking-[-0.28px]">{fmt(discount)}</span>
										{discountPct > 0 && <span className="text-[12px] leading-4 font-medium text-[#797979]">-{discountPct}%</span>}
									</span>
								</div>}
							<SummaryRow label={t("checkout.deliveryFee")} value={fmt(deliveryTotal)} />
							{freeDeliveryApplied && <div className="flex items-center justify-between gap-1 text-[#FA5C2E]" data-testid="free-delivery-row">
									<span className="text-[14px] leading-[15px] font-semibold tracking-[-0.28px]">
										{t("checkout.freeDeliveryPromo", {
                  defaultValue: "Бесплатная доставка по промокоду"
                })}
									</span>
									<span className="text-[14px] leading-[19px] font-bold tracking-[-0.28px] text-[#ABABAB] line-through">
										{fmt(deliveryFull - deliveryTotal)}
									</span>
								</div>}
						</div>
						{canSplitPromo && <label className="flex items-start gap-2 text-[14px] leading-[18px] text-[#161616]" data-testid="split-promo">
								<Checkbox checked={splitPromo} onCheckedChange={v_1 => setSplitPromo(Boolean(v_1))} disabled={!(payMethod === "card" || payMethod === "sbp") || useBalance} aria-label="Оформить товары со скидкой отдельным заказом" className="mt-0.5" />
								<span className="flex flex-col gap-0.5">
									<span>Оформить товары со скидкой отдельным заказом</span>
									<span className="text-[12px] leading-4 text-[#1F1F1F]/48">
										{payMethod === "card" || payMethod === "sbp" ? useBalance ? "Недоступно при оплате балансом" : "Будет два заказа, каждый оплачивается отдельно. Доставка считается для каждого заказа." : "Доступно при оплате картой или СБП"}
									</span>
								</span>
							</label>}
						{partial ? <p className="text-[12px] leading-4 text-[#1F1F1F]/48" data-testid="partial-promo-note">
								Оформляются только выбранные товары. Промокод действует при оформлении всей корзины.
							</p> : <div className="flex flex-col gap-1">
								<Input variant="square" value={cart?.promoCode ?? promoInput} readOnly={Boolean(cart?.promoCode)} onChange={e_5 => setPromoInput(e_5.target.value)} onKeyDown={e_6 => {
              if (e_6.key === "Enter") void submitPromo();
            }} onBlur={() => {
              if (!cart?.promoCode && promoInput.trim()) void submitPromo();
            }} placeholder={t("checkout.promoCode", {
              defaultValue: "Промокод"
            })} aria-label={t("checkout.promoCode", {
              defaultValue: "Промокод"
            })} className="font-normal" />
								{cart?.promoCode && <button type="button" disabled={promoBusy} onClick={() => void dropPromo()} className="self-start text-[12px] leading-4 text-[#1F1F1F]/48 hover:text-[#1F1F1F]">
										{t("cart.clearPromo", {
                defaultValue: "Убрать промокод"
              })}
									</button>}
							</div>}
						<div className="flex items-center justify-between gap-1 text-[#161616]">
							<span className="text-[16px] leading-[18px] font-semibold tracking-[-0.32px]">{t("checkout.total")}</span>
							<span className="text-[24px] leading-[33px] font-bold tracking-[-0.48px]">{fmt(total)}</span>
						</div>
						{balanceApplied > 0 && <div className="-mt-2 flex flex-col gap-1 border-t border-[#1F1F1F]/8 pt-2 text-[14px] text-[#1F1F1F]">
								<div className="flex justify-between">
									<span className="text-[#1F1F1F]/64">{t("checkout.balanceApplied")}</span>
									<span>−{fmt(balanceApplied)}</span>
								</div>
								<div className="flex justify-between font-medium">
									<span>{t("checkout.remainingToPay")}</span>
									<span data-testid="remaining-to-pay">{fmt(payableTotal)}</span>
								</div>
							</div>}
					</div>

					<Button disabled={submitting || Boolean(done)} onClick={() => void onSubmit()} data-testid="place-order" block className="mt-2.5">
						{submitLabel}
					</Button>

					{/* Документы 12/16 #000 через 10. PDF открываются в новой вкладке (A-34). */}
					<div className="mt-2.5 flex flex-col items-start gap-2.5 text-[12px] leading-4 text-black" data-testid="checkout-legal">
						<p>
							Нажимая «{payMethod === "invoice" ? "Сформировать счёт" : "Оплатить"}», вы соглашаетесь{" "}
							<a href={LEGAL.terms} target="_blank" rel="noreferrer" className="underline underline-offset-2">
								с условиями продажи
							</a>{" "}
							и даёте{" "}
							<a href={LEGAL.pdnConsent} target="_blank" rel="noreferrer" className="underline underline-offset-2">
								согласие на обработку персональных данных
							</a>
						</p>
						<a href={LEGAL.delivery} target="_blank" rel="noreferrer" className="underline underline-offset-2">
							Условия доставки и возврата
						</a>
						<Popover>
							<PopoverTrigger asChild>
								<button type="button" className="underline underline-offset-2">
									Данные продавца
								</button>
							</PopoverTrigger>
							<PopoverContent align="start" className="w-64 rounded-[10px] p-3 text-[14px] text-[#1F1F1F]">
								{shopNames.length > 0 ? <ul className="flex flex-col gap-1">
										{shopNames.map(n => <li key={n}>{n}</li>)}
									</ul> : <p className="text-[#1F1F1F]/64">Продавцы появятся после выбора адреса доставки</p>}
							</PopoverContent>
						</Popover>
					</div>
				</aside>
			</div>

			{/* ── Модалки ── */}
			<RecipientListModal open={modal === "recipients"} onOpenChange={o_20 => !o_20 && setModal(null)} recipients={recipients} selectedId={recipient?.id ?? null} onSelect={r_0 => {
      setRecipient({
        id: r_0.id,
        firstName: r_0.firstName,
        lastName: r_0.lastName,
        phone: r_0.phone
      });
      setModal(null);
    }} onAdd={() => {
      setEditingRecipient(null);
      setModal("recipientForm");
    }} onEdit={r_1 => {
      setEditingRecipient(r_1);
      setModal("recipientForm");
    }} onChanged={() => void recipientsQ.refetch()} />
			<RecipientFormModal open={modal === "recipientForm"} onOpenChange={o_21 => !o_21 && setModal(null)} recipient={editingRecipient} persist={Boolean(buyerUser)} defaults={recipients.length === 0 && recipient ? recipient : undefined} onSaved={r_2 => {
      setRecipient({
        id: r_2.id,
        firstName: r_2.firstName,
        lastName: r_2.lastName,
        phone: r_2.phone
      });
      void recipientsQ.refetch();
      setModal(null);
    }} />
			<AddressListModal open={modal === "addresses"} onOpenChange={o_22 => !o_22 && setModal(null)} addresses={addresses} selectedId={addressId} onSelect={a_2 => {
      applyAddress(a_2);
      if (!keepModeOnAddress.current) setMode("courier");
      keepModeOnAddress.current = false;
      setModal(null);
    }} onAdd={() => {
      setModal(null);
      setPickerCourier(emptyCourierAddress());
      setPicker("courier");
    }} onEdit={a_3 => {
      setEditingAddress(a_3);
      setModal("addressEdit");
    }} onChanged={() => void addressesQ.refetch()} />
			<AddressEditModal open={modal === "addressEdit"} onOpenChange={o_23 => !o_23 && setModal(null)} address={editingAddress} contact={contactFor()} onSaved={a_4 => {
      void addressesQ.refetch();
      if (a_4.id === addressId || !addressId) applyAddress(a_4);
      setModal("addresses");
    }} />
			{selectedPvz?.pvzAddress && <PickupInfoModal open={modal === "pvzInfo"} onOpenChange={o_24 => !o_24 && setModal(null)} point={{
      carrier: pvzCarrier,
      address: selectedPvz.pvzAddress,
      workTime: cityPoints.find(p_3 => p_3.code === selectedPvz.pvzCode)?.workTime ?? savedPoints.find(p_2 => p_2.code === selectedPvz.pvzCode)?.workTime
    }} onChange={() => setModal(savedPoints.length > 0 ? "pvzList" : (setPicker("pickup"), null))} />}
			<PickupListModal open={modal === "pvzList"} onOpenChange={o_25 => !o_25 && setModal(null)} carrier={pvzCarrier} points={savedPoints} selectedCode={selectedPvz?.pvzCode} isAvailable={p_4 => p_4.carrier === pvzCarrier && (!p_4.city || p_4.city.toLowerCase() === dest.city.toLowerCase()) && (pointsQ.isSuccess ? cityPoints.some(c_1 => c_1.code === p_4.code) : true)} onSelect={p_5 => {
      pickPoint(p_5);
      setModal(null);
    }} onAdd={() => {
      setModal(null);
      setPicker("pickup");
    }} onChanged={() => void savedPointsQ.refetch()} />
			<AddCardModal open={modal === "addCard"} onOpenChange={o_26 => !o_26 && setModal(null)} isFirst={methods.length === 0} onSaved={m_5 => {
      void methodsQ.refetch();
      setPayKey(`card:${m_5.id}`);
      setModal(null);
    }} />
			<InvoiceRequisitesModal open={modal === "invoice"} onOpenChange={o_27 => !o_27 && setModal(null)} billing={billing} onSaved={() => {
      void billingQuery.refetch();
      setPayKey("invoice");
      setModal(null);
    }} />
			<InvoicePreviewModal open={modal === "invoicePreview"} onOpenChange={o_28 => !o_28 && setModal(null)} invoice={invoice} loading={invoiceLoading} onDownload={() => invoice && printInvoice(invoice)} onGoToOrder={() => done && goToOrder(done.orderId)} />
			<EmailModal open={modal === "email"} onOpenChange={o_29 => {
      if (o_29) return;
      setModal(null);
      emailResolve.current?.(null);
      emailResolve.current = null;
    }} initial={buyerProfile?.email ?? ""} purpose={payMethod === "invoice" ? "invoice" : "receipt"} onSaved={async email_0 => {
      if (buyerUser) {
        try {
          await updateProfile({
            email: email_0
          });
        } catch (e_7) {
          toast.error(e_7 instanceof Error ? e_7.message : "Не удалось сохранить почту");
          return;
        }
      }
      const resolve_0 = emailResolve.current;
      emailResolve.current = null;
      setModal(null);
      resolve_0?.(email_0);
    }} />
			<OrderDoneModal open={done?.kind === "paid"} onOpenChange={o_30 => !o_30 && navigate("/catalog")} items={(done?.items ?? []).map(i_12 => ({
      id: i_12.id,
      photo: i_12.photo,
      name: i_12.name,
      qty: i_12.qty
    }))} onGoToOrder={() => done && goToOrder(done.orderId)} onContinue={() => navigate("/catalog")} nextPayment={done?.next ? {
      label: `Второй заказ${done.next.number ? ` №${done.next.number}` : ""} на ${fmt(done.next.total)} ждёт оплаты`,
      onPay: () => done.next && navigate(`/pay/${encodeURIComponent(done.next.id)}`)
    } : undefined} />
			<DeliveryPicker open={picker !== null} onClose={() => {
      keepModeOnAddress.current = false;
      setPicker(null);
    }} tab={picker ?? "pickup"} onTabChange={tab => {
      if (tab === "courier") setPickerCourier(courier);
      setPicker(tab);
    }} pickupAvailable={!dest.city || groups.length === 0 || modeAvailable("pickup")} courierAvailable={!dest.city || groups.length === 0 || modeAvailable("courier")} city={dest.city} onCityPick={c_2 => {
      destTouched.current = true;
      setDest({
        country: c_2.country || dest.country,
        city: c_2.city,
        postalCode: c_2.postalCode ?? "",
        region: c_2.region
      });
    }} points={cityPoints} pointsLoading={pointsQ.isFetching} selectedCode={selectedPvz?.pvzCode} carrier={pvzCarrier} pickupPriceLabel={pvzOption ? [fmt(pvzOption.deliverySum), pvzOption.deliveryDays].filter(Boolean).join(",  ") : undefined} onPickPoint={p_6 => {
      pickPoint(p_6);
      setMode("pickup");
      setPicker(null);
    }} courier={pickerCourier} onCourierChange={setPickerCourier} onCourierSubmit={() => void submitPickerCourier()} courierBusy={courierBusy} pvzRequired={groups.length === 0 || groups.some(g_14 => g_14.options.some(o_31 => o_31.toPickupPoint && o_31.carrier === "sdek"))} onChoosePickup={() => {
      setMode("pickup");
      setPicker(null);
    }} />
		</div>;
}

// Строка сводки: подпись 16/18 w600 и сумма 16/22 w700 #161616.
function SummaryRow(t0) {
  const $ = _c(7);
  const {
    label,
    value
  } = t0;
  let t1;
  if ($[0] !== label) {
    t1 = <span className="text-[16px] leading-[18px] font-semibold tracking-[-0.32px]">{label}</span>;
    $[0] = label;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== value) {
    t2 = <span className="text-[16px] leading-[22px] font-bold tracking-[-0.32px]">{value}</span>;
    $[2] = value;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <div className="flex items-center justify-between gap-1 text-[#161616]">{t1}{t2}</div>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}

// Чип варианта доставки (кадр 3924:17790) — DS `Chip` sm: h32 r96 на #1F1F1F 3 %
// с обводкой 4 % 0,5, текст 14/18 w500, бейдж цены r96 10/13 w500 (#6982BA,
// бесплатно — #32D47D). Выбранный — как у DS: #FA5C2E 5 % с обводкой #FA5C2E.
function DeliveryChip(t0) {
  const $ = _c(13);
  const {
    active,
    disabled,
    title,
    onClick,
    label,
    price,
    free,
    testId
  } = t0;
  const t1 = free ? "bg-ds-success" : "bg-[#6982BA]";
  let t2;
  if ($[0] !== t1) {
    t2 = cn("inline-flex h-4 items-center rounded-full px-0.5 text-[10px] leading-[13px] font-medium text-white", t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== price || $[3] !== t2) {
    t3 = <span className={t2}>{price}</span>;
    $[2] = price;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== active || $[6] !== disabled || $[7] !== label || $[8] !== onClick || $[9] !== t3 || $[10] !== testId || $[11] !== title) {
    t4 = <Chip selected={active} disabled={disabled} title={title} onClick={onClick} data-testid={testId}>{label}{t3}</Chip>;
    $[5] = active;
    $[6] = disabled;
    $[7] = label;
    $[8] = onClick;
    $[9] = t3;
    $[10] = testId;
    $[11] = title;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  return t4;
}

// Карточка способа оплаты 100×68 r16, отступ 10 (кадр 3919:17413): иконка в
// белом круге 24 и подпись 12/13 w500 внизу; выбранная — заливка #FA5C2E 10 %
// и обводка #FA5C2E 0.5.
function PayTile(t0) {
  const $ = _c(11);
  const {
    selected,
    onClick,
    icon,
    label,
    testId
  } = t0;
  const t1 = selected ? "bg-[#FA5C2E]/10 ring-[0.5px] ring-[#FA5C2E] ring-inset" : "bg-[#F8F8F8] hover:bg-[#F2F2F2]";
  let t2;
  if ($[0] !== t1) {
    t2 = cn("flex h-[68px] w-[100px] shrink-0 flex-col justify-between rounded-[16px] p-2.5 text-left outline-none transition-colors focus-visible:ring-2 focus-visible:ring-osmix-tangelo/40", t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== label) {
    t3 = <span className="truncate text-[12px] leading-[13px] font-medium tracking-[-0.24px] text-[#161616]">{label}</span>;
    $[2] = label;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== icon || $[5] !== onClick || $[6] !== selected || $[7] !== t2 || $[8] !== t3 || $[9] !== testId) {
    t4 = <button type="button" onClick={onClick} data-testid={testId} aria-pressed={selected} className={t2}>{icon}{t3}</button>;
    $[4] = icon;
    $[5] = onClick;
    $[6] = selected;
    $[7] = t2;
    $[8] = t3;
    $[9] = testId;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  return t4;
}
function IconCircle(t0) {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <span className="grid size-6 place-items-center rounded-full bg-white text-[#161616]">{children}</span>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}

// Банк (колонны) — иконка сохранённой карты из макета.
function BankIcon() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <IconCircle><svg viewBox="0 0 16 16" className="size-4" fill="none" stroke="currentColor" strokeWidth="1.2" aria-hidden="true"><path d="M2 6h12M8 2.5 14 6H2l6-3.5ZM3.5 6v5.5M6.5 6v5.5M9.5 6v5.5M12.5 6v5.5M2 13.5h12" /></svg></IconCircle>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}

// Логотип СБП (цветной «флажок»); `single` — только СБП без банка.
function SbpIcons(t0) {
  const $ = _c(3);
  const {
    single
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <svg viewBox="0 0 14 16" className="size-4" aria-hidden="true"><path fill="#5B57A2" d="M0 3.4 2 7v2.1L0 12.5z" /><path fill="#D90751" d="m7.5 5.6 1.8-1.1h3.7L7.5 7.9z" /><path fill="#FAB718" d="M7.5 3.4v4.6l-2-1.2V0z" /><path fill="#ED6F26" d="M13 4.5H9.3L7.5 3.4 5.5 0z" /><path fill="#1487C9" d="M9.3 11.4 2 7 0 3.4l13 8.1z" /><path fill="#017F36" d="m5.5 16 2-3.5 1.8-1.1H13z" /><path fill="#984995" d="M0 12.5 5.5 9.1 3.7 8 0 12.5z" /></svg>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const logo = t1;
  if (single) {
    let t2;
    if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <IconCircle>{logo}</IconCircle>;
      $[1] = t2;
    } else {
      t2 = $[1];
    }
    return t2;
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <span className="flex"><BankIcon /><span className="-ml-1.5 grid size-6 place-items-center rounded-full bg-[#F8F8F8] shadow-[-2px_0_4px_rgba(0,0,0,0.12)]">{logo}</span></span>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}